/**
 * 날짜와 관련된 함수
 */

import "./format";

/**
 * 입력값을 날짜 타입으로 변환시킨다.
 */

export const getDate = (date, includeTime) => {
  if (!date) {
    date = new Date();
  } else if (typeof date == "string") {
    date = new Date(date);
  }
  return includeTime
    ? new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds()
      )
    : new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * 입력값을 날짜 포맷으로 변환시킨다.
 */

export const getCalendar = (date, format) => {
  date = getDate(date);
  if (!format) format = "yyyy-mm-dd";
  return date.format(format);
};

export const getClock = (date, format) => {
  date = getDate(date, true);
  if (!format) format = "hh:MM";
  return date.format(format);
};

export const getToday = (format) => {
  return getCalendar(null, format);
};

/**
 * 주어진 날짜의 월을 반환한다.
 * @param date - Date 객체, 날짜 문자열, 또는 undefined (현재 날짜 사용)
 * @param format - 반환할 월의 형식 (기본값: "mm")
 * @returns 형식에 맞춘 월 문자열
 */
export const getMonth = (date, format = "mm") => {
  const targetDate = getDate(date);
  const month = targetDate.getMonth() + 1; // getMonth()는 0-11을 반환하므로 1을 더함

  switch (format) {
    case "m":
      return month.toString();
    case "mm":
      return month.toString().padStart(2, "0");
    default:
      return month.toString().padStart(2, "0");
  }
};

/**
 * 주어진 날짜의 연도를 반환한다.
 * @param date - Date 객체, 날짜 문자열, 또는 undefined (현재 날짜 사용)
 * @param format - 반환할 연도의 형식 (기본값: "yyyy")
 * @returns 형식에 맞춘 연도 문자열
 */
export const getYear = (date, format = "yyyy") => {
  const targetDate = getDate(date);
  const year = targetDate.getFullYear();

  switch (format) {
    case "yy":
      return year.toString().slice(-2);
    case "yyyy":
      return year.toString();
    default:
      return year.toString();
  }
};

/**
 * 주어진 날짜가 현재 월인지 확인한다.
 * @param date - Date 객체, 날짜 문자열, 또는 undefined (현재 날짜 사용)
 * @returns 주어진 날짜가 현재 월이면 true, 아니면 false
 */
export const isCurrentMonth = (date) => {
  const targetDate = getDate(date);
  const currentDate = new Date();

  return (
    targetDate.getFullYear() === currentDate.getFullYear() &&
    targetDate.getMonth() === currentDate.getMonth()
  );
};

/**
 * 현재 월의 1일을 반환한다.
 */
export const getFirstDayOfMonth = (format = "yyyy-mm-dd") => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  return formatDate(firstDay, format);
};

/**
 * 이전 달의 1일을 반환한다.
 */
export const getFirstDayOfPreviousMonth = (format = "yyyy-mm-dd") => {
  const today = new Date();
  const firstDayOfPreviousMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    1
  );
  return formatDate(firstDayOfPreviousMonth, format);
};

/**
 * 이전 달의 마지막 날을 반환한다.
 */
export const getLastDayOfPreviousMonth = () => {
  const today = new Date();
  const lastDayOfPreviousMonth = new Date(
    today.getFullYear(),
    today.getMonth(),
    0
  );

  const year = lastDayOfPreviousMonth.getFullYear().toString().slice(-2); // 연도의 마지막 2자리
  const month = (lastDayOfPreviousMonth.getMonth() + 1)
    .toString()
    .padStart(2, "0");
  const day = lastDayOfPreviousMonth.getDate().toString().padStart(2, "0");

  return `${year}.${month}.${day}`;
};

// 주어진 날짜가 이전 달이거나 그 이전인지 확인하는 함수
export const isPreviousMonthOrEarlier = (date) => {
  const currentDate = new Date();
  const givenDate = new Date(date);

  // 현재 달의 첫 날
  const firstDayOfCurrentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    1
  );

  // 주어진 날짜가 현재 달의 첫 날보다 이전인지 확인
  return givenDate < firstDayOfCurrentMonth;
};

export const compareTwoMonths = (date1, date2) => {
  const givenDate1 = new Date(date1);
  const givenDate2 = new Date(date2);

  return givenDate1 < givenDate2;
};

// 날짜 포맷팅을 위한 헬퍼 함수
export const formatDate = (date, format) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return format
    .replace("yyyy", String(year))
    .replace("mm", month)
    .replace("dd", day)
    .replace("hh", hours)
    .replace("MM", minutes)
    .replace("ss", seconds);
};

/**
 * 날짜에 입력한 년, 월, 일 만큼 더하거나 뺀다.
 */

export const getDateAdd = (date, year, month, day) => {
  return getCalendar(getDate(date).add(year, month, day));
};

/**
 * 날짜를 비교해 몇 일이 경과되었는지 계산한다.
 */

export function getDateElapse(startDate, endDate, unitType) {
  startDate = getDate(startDate, unitType);
  endDate = getDate(endDate, unitType);
  const startTime = startDate.getTime();
  const endTime = endDate.getTime();
  if (isNaN(startTime) || isNaN(endTime)) return 0;

  let n = 1000 * 60 * 60 * 24;
  switch (unitType) {
    case "minute":
      n = 1000 * 60;
      break;
    case "hour":
      n = 1000 * 60 * 60;
      break;
    case "day":
      n = 1000 * 60 * 60 * 24;
      break;
    case "year":
      n = 1000 * 60 * 60 * 24 * 365;
      break;
  }
  return parseInt((endTime - startTime) / n);
}

/**
 * 날짜를 문자 형태로 변환시킨다. (작업 중)
 */

export const getDateText = (date, maxPeriod) => {
  const period = getDateElapse(date, getCalendar());
  if (period >= maxPeriod) return;
  if (period === 0) {
    return "오늘";
  } else if (period === 1) {
    return "어제";
  } else if (1 < period && period < 7) {
    return period + "일 전";
  } else if (period === 7) {
    return "1주 전";
  } else {
    return date;
  }
};

export const getFormattedDate = (date, isDatetime) => {
  date = getDate(date, isDatetime);
  let format = "yyyy. sm. sd. sw";
  if (isDatetime) format += " · hh:MM";
  return date.format(format);
};

export const getFormattedDateTime = (date) => {
  return getFormattedDate(date, true);
};

export const specialFormatDate = (t) => {
  const minutes = getDateElapse(t, new Date(), "minute");
  if (minutes < 60) {
    // 방금 and N분 전
    if (minutes < 2) return "방금 전";
    else return minutes + "분 전";
  } else {
    // N시간 전
    const hours = getDateElapse(t, new Date(), "hour");
    if (hours < 24) return hours + "시간 전";
    else {
      // N일 전 - 1주 전 + 시간
      const dateText = getDateText(t, 8); //
      if (dateText) return dateText.concat(" ∙ ", getClock(t, "ap sh:MM"));
      else {
        // 날짜 + 시간
        const date =
          getCalendar(t, "yyyy") +
          ". " +
          getCalendar(t, "sm") +
          ". " +
          getCalendar(t, "sd") +
          ". ";
        const weekDay = getCalendar(t, "sw");
        const hour = getClock(t, "ap sh:MM");
        return date + weekDay.concat(" ∙ ", hour);
      }
    }
  }
};

export default {
  getDate,
  getCalendar,
  getDateAdd,
  getDateElapse,
  getDateText,
  specialFormatDate,
};

/**
 * 주간 수 결정법은 '주간 수 결정법 표준(분류번호 : KS A 5402)'에 따른다.
 * (KS A 5402는 폐지되고, KS X ISO8601으로 대체되었다.)
 * 아래 두 함수는 주차를 계산하기 위해 사용된다.
 */

const getStartDayOfWeek = (date) => {
  const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
  const week = startDate.getDay();
  let day = date.getDate();
  if (week > 4) day = 7 - week + 1 + 1;
  else day = 1 - week + 1;
  return day;
};

const getEndDayOfWeek = (date) => {
  const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const week = endDate.getDay();
  let day = endDate.getDate();
  if (week >= 4) day = day + (7 - week);
  else day = day - week;
  return day;
};

/**
 * 날짜가 포함된 월의 첫 주차의 시작 날짜를 계산한다.
 */

Date.prototype.getStartDateFirstWeekInMonth = function () {
  return new Date(this.getFullYear(), this.getMonth(), getStartDayOfWeek(this));
};

/**
 * 날짜가 포함된 월의 마지막 주차의 시작 날짜를 계산한다.
 */

Date.prototype.getEndDateLastWeekInMonth = function () {
  return new Date(this.getFullYear(), this.getMonth(), getEndDayOfWeek(this));
};

/**
 * 날짜가 포함된 월의 마지막 주차를 계산한다.
 */

Date.prototype.getLastWeekInMonth = function () {
  const startTime = this.getStartDateFirstWeekInMonth().getTime();
  const endTime = this.getEndDateLastWeekInMonth().getTime();
  return parseInt(((endTime - startTime) / (1000 * 60 * 60 * 24) + 1) / 7);
};

/**
 * 날짜가 포함된 월의 시작 날짜를 계산한다.
 */

Date.prototype.getStartDateInMonth = function () {
  return new Date(this.getFullYear(), this.getMonth(), 1);
};

/**
 * 날짜가 포함된 월의 마지막 날짜를 계산한다.
 */

Date.prototype.getEndDateInMonth = function () {
  return new Date(this.getFullYear(), this.getMonth() + 1, 0);
};

/**
 * 날짜가 포함된 주차의 시작 및 종료 날짜를 계산한다.
 * (주차는 월요일부터 일요일까지이고, 달력 기준인 경우 일요일부터 토요일까지이다.)
 */

Date.prototype.getStartDateInWeek = function (isCalendar) {
  let weekIndex = this.getDay();
  if (isCalendar) {
    this.setDate(this.getDate() - weekIndex);
  } else {
    if (!weekIndex) weekIndex = 7;
    this.setDate(this.getDate() - (weekIndex - 1));
  }
  return this;
};

Date.prototype.getEndDateInWeek = function (isCalendar) {
  return this.getStartDateInWeek(isCalendar).add(0, 0, 6);
};

/**
 * 날짜가 해당 년도의 몇 번째 주차인지 계산한다.
 */

Date.prototype.getWeek = function () {
  return this.getWeekInYear();
};

Date.prototype.getWeekInYear = function () {
  const startDateInWeek = this.getStartDateInWeek();
  const startDateInYear = new Date(
    this.getFullYear(),
    0,
    4
  ).getStartDateInWeek();
  return (
    Math.ceil(
      (startDateInWeek.getTime() - startDateInYear.getTime()) /
        (1000 * 60 * 60 * 24) /
        7
    ) + 1
  );
};

/**
 * 날짜가 해당 월의 몇 번째 주차인지 계산한다.
 */

Date.prototype.getWeekInMonth = function () {
  const startTime = this.getStartDateFirstWeekInMonth().getTime();
  const endTime = this.getEndDateLastWeekInMonth().getTime();
  const time = new Date(
    this.getFullYear(),
    this.getMonth(),
    this.getDate()
  ).getTime();
  if (time < startTime) {
    return new Date(
      this.getFullYear(),
      this.getMonth() - 1,
      1
    ).getLastWeekInMonth();
  }
  if (time > endTime) {
    return 1;
  }
  return Math.ceil(
    (parseInt((time - startTime) / (1000 * 60 * 60 * 24)) + 1) / 7
  );
};

/**
 * 날짜에 입력한 년, 월, 일 만큼 더하거나 뺀다.
 */

Date.prototype.add = function (year, month, day) {
  year = year ? Number(year) : 0;
  month = month ? Number(month) : 0;
  day = day ? Number(day) : 0;
  this.setFullYear(
    this.getFullYear() + year,
    this.getMonth() + month,
    this.getDate() + day
  );
  return this;
};

/**
 * 날짜를 문자 형식에 맞게 변환시킨다.
 */

Date.prototype.format = function (format) {
  const date = this;
  const weekName = [
    "일요일",
    "월요일",
    "화요일",
    "수요일",
    "목요일",
    "금요일",
    "토요일",
  ];
  const shortWeekName = ["일", "월", "화", "수", "목", "금", "토"];
  return format.replace(
    /(yyyy|yy|sm|zm|mm|sd|zd|dd|ww|sw|sh|hh|MM|ss|ap)/gi,
    function ($1) {
      const isAp = format.indexOf("ap") > -1 ? true : false;
      const hour = date.getHours() % 12;
      switch ($1) {
        case "yyyy":
          return date.getFullYear();
        case "yy":
          return (date.getFullYear() % 100).zf(2);
        case "sm":
          return (date.getMonth() + 1).sf(2);
        case "zm":
          return date.getMonth() + 1;
        case "mm":
          return (date.getMonth() + 1).zf(2);
        case "sd":
          return date.getDate().sf(2);
        case "zd":
          return date.getDate();
        case "dd":
          return date.getDate().zf(2);
        case "ww":
          return weekName[date.getDay()];
        case "sw":
          return shortWeekName[date.getDay()];
        case "hh":
          return isAp ? (hour ? hour : 12).zf(2) : date.getHours().zf(2);
        case "sh":
          return hour ? hour : 12;
        case "MM":
          return date.getMinutes().zf(2);
        case "ss":
          return date.getSeconds().zf(2);
        case "ap":
          return date.getHours() < 12 ? "오전" : "오후";
        default:
          return $1;
      }
    }
  );
};

/**
 * 날짜를 yyyy-mm-dd 문자 포맷으로 변환 시킨다.
 */

Date.prototype.getCalendar = function () {
  return getCalendar(this);
};

/**
 * 날짜 또는 시간을 UI 포멧으로 변환 시킨다.
 */

Date.prototype.getFormattedDate = function () {
  return this.format("yyyy. sm. sd. sw");
};

Date.prototype.getFormattedTime = function () {
  return this.format("hh:MM");
};

Date.prototype.getFormattedDatetime = function () {
  return this.format("yyyy. sm. sd. sw · hh:MM");
};

Date.prototype.getDayName = function () {
  return this.format("sw");
};
